import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Tag, Select } from "antd";
import CustomPagination from "../../components/pagination";
import StatusTabs from "../../components/statusTabs";
import SearchBar from "../../components/searchBar";
import ColumnVisibility from "../../components/columnVisibility";
import ChangePasswordModal from "../../components/modals/ChangePasswordModal";
import DateRangeFilter from "../../components/dateRangeFilter";
import { getAddOnsUserColumns, AddOnUserRecord } from "./columns";
import { useGetAllAddonsUsersQuery } from "../../services/membership";
import { useGetOpenTrainersQuery } from "../../services/trainer";
import { useCountries } from "../../hooks/useCountries";
import { useSelector } from "react-redux";

/** Format a Date as YYYY-MM-DD using local time. */
const toLocalDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

interface FilterDateRange {
  start: Date;
  end:   Date;
}

const ADDON_TYPE_OPTIONS = [
  { label: "All Types",         value: "all"               },
  { label: "Personal Training", value: "personal_training" },
  { label: "Pilates",           value: "pilates"           },
  { label: "Therapy",           value: "therapy"           },
  { label: "EMS",               value: "ems"               },
  { label: "Paid Locker",       value: "paid_locker"       },
  { label: "MMA",               value: "mma"               },
];

const SESSION_STATUS_OPTIONS = [
  { label: "All Sessions", value: "all"         },
  { label: "Live",         value: "in_progress" },
  { label: "Paused",       value: "paused"      },
  { label: "Completed",    value: "completed"   },
];

const SUB_CATEGORY_OPTIONS = [
  { label: "All Sub Categories", value: "all"    },
  { label: "Single",             value: "single" },
  { label: "Couple",             value: "couple" },
  { label: "Group",              value: "group"  },
];
import "./styles.scss";
import CommonTable from "../../components/commonTable";
import { formatDate } from "../../utils/date";
import usePermissions from "../../hooks/usePermissions";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddonRow {
  _id:               string;
  category:          string;
  subCategory:       string;
  isLinkedMember:    boolean;
  linkedNames:       string[];
  linkedMembers:     { id: string; name: string }[];
  primaryName:       string | null;
  primaryId:         string | null;
  planInfo:          string;
  trainer:           string;
  startDate:         string;
  endDate:           string;
  status:            string;
  sessions:          number | string;
  usedSessions:      number;
  remainingSessions: number;
  totalInvoiced:     number | string;
  totalPaid:         number | string;
  remainingDue:      number | string;
}

interface UserRow extends AddOnUserRecord {
  _id:    string;
  status: string;
  addons: AddonRow[];
}

interface VisibleColumns {
  branch:      boolean;
  name:        boolean;
  phoneNumber: boolean;
  profile:     boolean;
  gender:      boolean;
  totalAddons: boolean;
}

/* ─── Expand columns (static) ────────────────────────────────────────── */

const getAddonExpandColumns = (onMemberClick: (userId: string) => void) => [
  {
    title:     'Category',
    dataIndex: 'category',
    key:       'category',
    width:     160,
    render:    (text: string) => (
      <Tag color="purple" style={{ textTransform: 'capitalize' }}>{text || '-'}</Tag>
    ),
  },
  {
    title:     'Sub Category',
    dataIndex: 'subCategory',
    key:       'subCategory',
    width:     140,
    render:    (text: string) =>
      text && text !== '-'
        ? <Tag color="geekblue" style={{ textTransform: 'capitalize' }}>{text}</Tag>
        : '-',
  },
  {
    title:  'Linked Members',
    key:    'linkedMembers',
    width:  180,
    render: (_: unknown, r: AddonRow) => {
      const memberLink = (id: string, name: string) => (
        <a
          key={id}
          onClick={(e) => { e.stopPropagation(); onMemberClick(id); }}
          style={{ color: 'var(--primary, #1677ff)', cursor: 'pointer' }}
        >
          {name}
        </a>
      );

      if (r.isLinkedMember && r.primaryName && r.primaryId)
        return <span>Linked to {memberLink(r.primaryId, r.primaryName)}</span>;
      if (r.linkedMembers.length)
        return r.linkedMembers.map((m, i) => (
          <span key={m.id}>{i > 0 && ', '}{memberLink(m.id, m.name)}</span>
        ));
      if (r.linkedNames.length)
        return r.linkedNames.join(', ');
      return '-';
    },
  },
  { title: 'Plan Name',   dataIndex: 'planInfo',   key: 'planInfo',   width: 180, render: (text: string) => text || '-' },
  {
    title:  'Sessions (Used/Remaining)',
    key:    'sessions',
    width:  180,
    render: (_: unknown, r: AddonRow) =>
      r.sessions === '-' ? '-' : `${r.usedSessions} / ${r.remainingSessions}`,
  },
  { title: 'Trainer',        dataIndex: 'trainer',       key: 'trainer',       width: 140, render: (text: string)          => text || '-'  },
  { title: 'Start Date',     dataIndex: 'startDate',     key: 'startDate',     width: 120 },
  { title: 'End Date',       dataIndex: 'endDate',       key: 'endDate',       width: 120 },
  { title: 'Total Invoiced', dataIndex: 'totalInvoiced', key: 'totalInvoiced', width: 130, render: (v: number | string)    => v ?? '-'     },
  { title: 'Total Paid',     dataIndex: 'totalPaid',     key: 'totalPaid',     width: 110, render: (v: number | string)    => v ?? '-'     },
  { title: 'Remaining Due',  dataIndex: 'remainingDue',  key: 'remainingDue',  width: 130, render: (v: number | string)    => v ?? '-'     },
  {
    title:     'Status',
    dataIndex: 'status',
    key:       'status',
    width:     100,
    render:    (text: string) => (
      <Tag color={text === 'active' ? 'green' : text === 'freezed' ? 'blue' : 'red'}>
        {text?.toUpperCase() || '-'}
      </Tag>
    ),
  },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const AllAddOnsUsers = () => {
  const { hasPermission } = usePermissions();
  const { dialCodeMap } = useCountries();
  const navigate = useNavigate();

  const goToMemberAddons = (userId: string) => navigate(`/user-detail/${userId}/addon-service`);
  const addonExpandColumns = useMemo(() => getAddonExpandColumns(goToMemberAddons), []);

  const [page,             setPage]             = useState(1);
  const [limit,            setLimit]            = useState(10);
  const [searchText,       setSearchText]       = useState('');
  const [activeTab,        setActiveTab]        = useState('all');
  const [addonType,        setAddonType]        = useState('all');
  const [subCategory,      setSubCategory]      = useState('all');
  const [coachId,          setCoachId]          = useState('all');
  const [sessionStatus,    setSessionStatus]    = useState('all');
  const [dateRange,        setDateRange]        = useState<FilterDateRange | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser,     setSelectedUser]     = useState<UserRow | null>(null);
  const [expandedRowKey,   setExpandedRowKey]   = useState<string | null>(null);

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>({
    branch:      true,
    name:        true,
    phoneNumber: true,
    profile:     true,
    gender:      true,
    totalAddons: true,
  });

  // branch comes from the global header (Redux), not a per-page filter
  const selectedBranch = useSelector((state: any) => state.branch.selectedBranch);

  const { data: trainersData } = useGetOpenTrainersQuery({ page: 1, limit: 1000, branchId: selectedBranch || undefined });

  // reset to first page whenever the global branch changes
  useEffect(() => { setPage(1); }, [selectedBranch]);

  const trainerOptions = useMemo(() => {
    const td = trainersData as any;
    const list = td?.data ?? td?.trainers;
    const opts = Array.isArray(list)
      ? list.map((t: any) => ({ label: t?.user?.name ?? t?.name, value: t?._id ?? t?.id })).filter((o: any) => o.label && o.value)
      : [];
    return [{ label: 'All Trainers', value: 'all' }, ...opts];
  }, [trainersData]);

  const { data: apiData, isLoading } = useGetAllAddonsUsersQuery({
    page,
    limit,
    search:        searchText,
    status:        activeTab !== 'all' ? activeTab : undefined,
    addonType:     addonType !== 'all' ? addonType : undefined,
    subCategory:   subCategory !== 'all' ? subCategory : undefined,
    branchId:      selectedBranch || undefined,
    coachId:       coachId !== 'all' ? coachId : undefined,
    sessionStatus: sessionStatus !== 'all' ? sessionStatus : undefined,
    startDate:     dateRange?.start ? toLocalDateStr(dateRange.start) : undefined,
    endDate:       dateRange?.end   ? toLocalDateStr(dateRange.end)   : undefined,
  } as any);

  const usersData = useMemo<UserRow[]>(() => {
    const rows = (apiData as any)?.data;
    if (!Array.isArray(rows)) return [];

    // Build a userId → name lookup from the whole response so we can show the
    // names of linked couple/group members (the addon only stores their ids).
    const userNameMap = new Map<string, string>();
    rows.forEach((item: any) => {
      if (item.user?._id) userNameMap.set(item.user._id, item.user.name || item.user._id);
    });
    const nameFor = (id: string) => userNameMap.get(id) || id;

    // linkedUsers / primaryUser may arrive as full objects ({_id, name, ...})
    // or as plain ids (older shape) — normalise either form.
    const memberId   = (u: any) => (typeof u === 'string' ? u : u?._id);
    const memberName = (u: any) =>
      typeof u === 'string' ? nameFor(u) : (u?.name || u?.phoneNumber || u?._id || '-');

    return rows.map((item: any) => {
      const user       = item.user   || {};
      const addons     = item.addons || [];
      const firstAddon = addons[0]   || {};
      const branch     = firstAddon.branch || {};
      return {
        _id:         user._id,
        userId:      user._id,
        name:        user.name        || '-',
        branch:      branch.name      || '-',
        phoneNumber: user.phoneNumber || '-',
        countryCode: user.member?.countryCode || user.countryCode || '',
        profile:     user.photo       || null,
        gender:      user.gender      || '-',
        status:      firstAddon.status || '-',
        addons:      addons.map((addon: any): AddonRow => ({
          _id:               addon._id,
          category:          addon.plan?.addonType?.replace(/_/g, ' ') || '-',
          subCategory:       addon.subCategory || addon.plan?.subCategory || '-',
          isLinkedMember:    !!addon.isLinkedMember,
          linkedNames:       Array.isArray(addon.linkedUsers)
                               ? addon.linkedUsers
                                   .filter((u: any) => memberId(u) !== user._id) // drop self
                                   .map(memberName)
                               : [],
          linkedMembers:     Array.isArray(addon.linkedUsers)
                               ? addon.linkedUsers
                                   .filter((u: any) => memberId(u) !== user._id) // drop self
                                   .map((u: any) => ({ id: memberId(u), name: memberName(u) }))
                                   .filter((m: any) => m.id)
                               : [],
          primaryName:       addon.primaryUser
                               ? memberName(addon.primaryUser)
                               : (addon.primaryUserId ? nameFor(addon.primaryUserId) : null),
          primaryId:         addon.primaryUser
                               ? memberId(addon.primaryUser)
                               : (addon.primaryUserId || null),
          planInfo:          addon.plan?.name    || '-',
          trainer:           addon.trainer?.name || '-',
          startDate:         formatDate(addon.startDate),
          endDate:           formatDate(addon.expiryDate),
          status:            addon.status      || '-',
          sessions:          addon.totalSessions || '-',
          usedSessions:      addon.usedSessions      || 0,
          remainingSessions: addon.remainingSessions  || 0,
          totalInvoiced:     addon.totalInvoiced ?? '-',
          totalPaid:         addon.totalPaid     ?? '-',
          remainingDue:      addon.remainingDue  ?? '-',
        })),
      };
    });
  }, [apiData]);

  const summary = (apiData as any)?.summary || {};
  const tabsData = [
    { key: 'all',      label: 'All',      count: summary.all      ?? (apiData as any)?.total ?? 0 },
    { key: 'active',   label: 'Active',   count: summary.active   ?? 0 },
    { key: 'inactive', label: 'Inactive', count: summary.inactive ?? 0 },
    { key: 'expired',  label: 'Expired',  count: summary.expired  ?? 0 },
  ];

  const columnsWithHandlers = getAddOnsUserColumns(hasPermission, dialCodeMap);
  const columns = columnsWithHandlers.filter((col: any) => visibleColumns[col.key as keyof VisibleColumns]);

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey as keyof VisibleColumns] }));
  };

  const handlePasswordModalCancel = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
  };

  const expandable = {
    expandedRowKeys:    expandedRowKey ? [expandedRowKey] : [],
    onExpand:           (expanded: boolean, record: UserRow) =>
                          setExpandedRowKey(expanded ? record._id : null),
    expandedRowRender:  (record: UserRow) => (
      <Table
        columns={addonExpandColumns}
        dataSource={record.addons}
        rowKey="_id"
        pagination={false}
        size="small"
        bordered
        style={{ margin: '8px 0' }}
      />
    ),
    rowExpandable: (record: UserRow) => (record.addons?.length ?? 0) > 0,
    expandIcon: ({ expanded, onExpand, record }: { expanded: boolean; onExpand: (record: UserRow, e: React.MouseEvent) => void; record: UserRow }) => (
      <button
        onClick={(e) => onExpand(record, e)}
        style={{
          width: 22, height: 22,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--hover-bg)',
          border: '1px solid var(--muted)',
          borderRadius: 5,
          color: 'var(--sider-text)',
          cursor: 'pointer',
          fontSize: 14,
          lineHeight: 1,
          padding: 0,
          transition: 'background .15s, border-color .15s',
        }}
      >
        {expanded ? '−' : '+'}
      </button>
    ),
  };

  return (
    <div className="all-users-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar value={searchText} onChange={setSearchText} placeholder="Search add-on users..." />
          <StatusTabs activeTab={activeTab} onTabChange={setActiveTab} tabs={tabsData} />
        </div>
        <div className="right-col" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {hasPermission('9-3-trainer-filter') && (
            <Select
              value={coachId}
              onChange={(val: string) => { setCoachId(val); setPage(1); }}
              options={trainerOptions}
              showSearch
              optionFilterProp="label"
              style={{ width: 170, height: 41 }}
              placeholder="All Trainers"
            />
          )}
          <Select
            value={addonType}
            onChange={(val: string) => { setAddonType(val); setPage(1); }}
            options={ADDON_TYPE_OPTIONS}
            style={{ width: 170, height: 41 }}
            placeholder="All Types"
          />
          <Select
            value={subCategory}
            onChange={(val: string) => { setSubCategory(val); setPage(1); }}
            options={SUB_CATEGORY_OPTIONS}
            style={{ width: 180, height: 41 }}
            placeholder="All Sub Categories"
          />
          {/* <Select
            value={sessionStatus}
            onChange={(val: string) => { setSessionStatus(val); setPage(1); }}
            options={SESSION_STATUS_OPTIONS}
            style={{ width: 160, height: 41 }}
            placeholder="All Sessions"
          /> */}
          <DateRangeFilter
            value={dateRange}
            onChange={(range: FilterDateRange) => { setDateRange(range); setPage(1); }}
          />
          <ColumnVisibility
            columns={columnsWithHandlers}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
          />
        </div>
      </div>

      <div className="users-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={usersData}
          loading={isLoading}
          pagination={false}
          rowKey={(record: any) => record._id || record.id}
          scroll={{ x: 900 }}
          expandable={expandable}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={(apiData as any)?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <ChangePasswordModal
        visible={showPasswordModal}
        onCancel={handlePasswordModalCancel}
        selectedUser={selectedUser}
        userType="user"
      />
    </div>
  );
};

export default AllAddOnsUsers;
